import { Collection, Database, Q } from '@nozbe/watermelondb';

import Organisation from './Organisation';
import {
    InventoryChangeModel,
    InventoryChangePayload,
} from '../../types/InventoryChange';
import { DBServiceOptionsWithImages } from '../../types/dbService';
import { DatabaseValidation } from '../../types/validation';
import {
    addPreparedDataBatch,
    mapRecord,
    PreparedInventoryChangeData,
} from './helpers/inventoryChange/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddInventoryChangeAction,
    prepareCreateInventoryChange,
} from './helpers/inventoryChange/inventoryChangeAdd';

class InventoryChange {
    private database: Database;
    private collection: Collection<InventoryChangeModel>;
    private table = 'inventory_changes' as const;
    private options: DBServiceOptionsWithImages & {
        validation: DatabaseValidation;
    };

    constructor(
        options: DBServiceOptionsWithImages & {
            validation: DatabaseValidation;
        },
    ) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    getAll() {
        return this.collection.query().fetch();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    async getByParam(param: string, value: any) {
        return this.collection.query(Q.where(param, value)).fetch();
    }

    async add(payload: InventoryChangePayload, userId: string) {
        return await this.database.write(async () => {
            const createdInventoryChange = await this.prepareAdd(
                payload,
                userId,
            );
            const preparedData: PreparedInventoryChangeData[] = [
                mapRecord(createdInventoryChange, this.table, 'add'),
            ];

            validateEntityRecords(
                preparedData,
                this.options.validation,
            );
            await addPreparedDataBatch(preparedData, this.database);

            logAddInventoryChangeAction({
                inventoryChange: createdInventoryChange,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });

            return createdInventoryChange;
        });
    }

    async prepareAdd(payload: InventoryChangePayload, userId: string) {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        return prepareCreateInventoryChange({
            payload,
            userId,
            organisationID,
            collection: this.collection,
        });
    }
}

export default InventoryChange;
